import { type ReactNode, useId, useRef } from 'react';
import type { Placement } from 'react-aria';
import { createPortal } from 'react-dom';
import { useNodeDimensions } from '@gonfalon/dom/useNodeDimensions';
import type { ListBoxProps } from '@launchpad-ui/components';
import {
  ComboBox,
  Group,
  IconButton,
  Input,
  ListBox,
  Popover,
  ProgressBar,
  TagGroup,
  TagList,
} from '@launchpad-ui/components';
import { clsx } from 'clsx';

import { RequiredLabel } from '../RequiredLabel/RequiredLabel';

import styles from './MultiComboBox.module.css';

export interface MultiComboBoxProps<T extends object> extends Partial<Omit<ListBoxProps<T>, 'renderEmptyState'>> {
  label?: ReactNode;
  description?: ReactNode;
  id?: string;
  placeholder?: string;
  className?: string;
  selectedKeys: string[];
  setFilterText?: (text: string) => void;
  filterText: string;
  isRequired?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  placement?: Placement;
  tags: () => ReactNode;
  onBlur: () => void;
  onKeyDown?: (e: { key: string; defaultPrevented: boolean }) => void;
  renderEmptyState?: (inputValue: string) => ReactNode;
}

export function MultiComboBox<T extends object>(props: MultiComboBoxProps<T>) {
  const {
    label,
    description,
    placement = 'bottom end',
    id = 'search-items',
    placeholder = 'Select items',
    className,
    selectionMode,
    items,
    selectedKeys,
    setFilterText,
    filterText,
    isRequired,
    isLoading,
    isDisabled,
    tags,
    onBlur,
    onKeyDown,
    onSelectionChange,
    renderEmptyState,
    onClick,
    children,
  } = props;
  const [node, dimensions] = useNodeDimensions<HTMLDivElement>();
  const tagGroupId = useId();
  const tagsRef = useRef<HTMLDivElement>(null);

  return (
    <div className={clsx(styles.multiComboBox, className)}>
      {tagsRef.current &&
        createPortal(
          <TagGroup
            id={tagGroupId}
            aria-label="Selected items"
            onRemove={
              isDisabled
                ? undefined
                : (selected) => {
                    const [, itemId] = String(selected.values().next().value).split(':');
                    onSelectionChange?.(new Set(selectedKeys.filter((item) => item !== itemId)));
                  }
            }
          >
            <TagList>{tags()}</TagList>
          </TagGroup>,
          tagsRef.current,
          tagGroupId,
        )}

      <ComboBox
        className={styles.comboBox}
        aria-label="Available options"
        items={items}
        isDisabled={isDisabled}
        onSelectionChange={(key) => {
          if (key) {
            onSelectionChange?.(new Set(selectedKeys.concat(String(key))));
          }
          setFilterText?.('');
        }}
        onClick={onClick}
        onBlur={onBlur}
        onKeyDown={(e) => {
          if (e.code === 'Escape') {
            setFilterText?.('');
            e.stopPropagation();
            onBlur?.();
          }
          onKeyDown?.(e);
        }}
        allowsEmptyCollection
      >
        {label && (
          <RequiredLabel htmlFor={id} isRequired={isRequired}>
            {label}
          </RequiredLabel>
        )}
        {description && (
          <span className={styles.description} slot="description">
            {description}
          </span>
        )}
        <Group
          ref={node}
          className={clsx(styles.group, {
            [styles.emptyTags]: selectedKeys.length === 0,
          })}
          aria-label="Search and filter"
        >
          <span className={styles.container} ref={tagsRef} />
          <Input
            id={id}
            aria-label="Search items"
            placeholder={placeholder}
            onKeyDownCapture={(e) => {
              if (e.code === 'Backspace' && filterText.length === 0) {
                onSelectionChange?.(new Set(selectedKeys.slice(0, -1)));
              }
            }}
            value={filterText}
            onChange={({ target: { value } }) => setFilterText?.(value)}
          />
          {isLoading && <ProgressBar aria-label="Loading…" isIndeterminate size="small" />}
          <IconButton aria-label="Show options" icon="chevron-down" size="small" variant="minimal" />
        </Group>
        <Popover maxHeight={320} placement={placement} triggerRef={node} shouldFlip={false} isNonModal>
          <ListBox<T>
            className={styles.listBox}
            aria-label="Available options"
            escapeKeyBehavior="none"
            selectionMode={selectionMode}
            style={{ minWidth: dimensions.width ? dimensions.width - 20 : undefined }}
            renderEmptyState={() => renderEmptyState?.(filterText) ?? null}
          >
            {children}
          </ListBox>
        </Popover>
      </ComboBox>
    </div>
  );
}
